import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import VisualPlan from 'app/frontend/types/custom/VisualPlan'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import useVisualPlanChannel from './useVisualPlanChannel'

type Options = {
  owner?: RaidPlanOwner
  visualId?: number
}

const useVisualPlanLiveSync = ({ owner, visualId }: Options) => {
  const queryClient = useQueryClient()

  const handlePlanUpdated = useCallback(
    (updated: VisualPlan) => {
      if (owner) {
        queryClient.setQueryData(
          ['visualPlan', { owner, visualId: updated.id }],
          updated,
        )
      }
      queryClient.setQueriesData(
        { queryKey: ['visualPlans'] },
        (previous: VisualPlan[] | undefined) => {
          if (!previous) return previous
          let hasMatch = false
          const next = previous.map((visual) => {
            if (visual.id !== updated.id) return visual
            hasMatch = true
            return updated
          })
          return hasMatch ? next : previous
        },
      )
    },
    [owner, queryClient],
  )

  const handlePlanDeleted = useCallback(
    (planId: number) => {
      if (owner) {
        queryClient.removeQueries({
          queryKey: ['visualPlan', { owner, visualId: planId }],
        })
      }
      queryClient.setQueriesData(
        { queryKey: ['visualPlans'] },
        (previous: VisualPlan[] | undefined) => {
          if (!previous) return previous
          const next = previous.filter((visual) => visual.id !== planId)
          return next.length === previous.length ? previous : next
        },
      )
    },
    [owner, queryClient],
  )

  useVisualPlanChannel({
    visualId,
    onPlanUpdated: handlePlanUpdated,
    onPlanDeleted: handlePlanDeleted,
  })
}

export default useVisualPlanLiveSync
